/**
 * Upgrade / Pricing Page with Structured Enterprise Inquiry
 *
 * The Enterprise CTA is a qualification intake form, not a generic contact form.
 * It captures org type, use case, project scope, and urgency to qualify
 * institutional leads (DFIs, governments, corporates).
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import {
  CheckCircle2, X, RefreshCw, Shield, Globe, FileText, Zap,
  Building2, ChevronDown, Check, AlertCircle
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const TIERS = [
  {
    key:          "free",
    name:         "Free",
    price:        "$0",
    interval:     "",
    badge:        null,
    highlight:    false,
    description:  "Project onboarding and basic eligibility screening",
    tagline:      "Start measuring",
    features: [
      "Project onboarding + CAI structuring",
      "V1 baseline measurement",
      "Climate Readiness Score (CRS) + A6RS eligibility scores",
      "Basic evidence upload",
      "Single project assessment",
    ],
    not_included: ["External reconciliation", "Portfolio reports", "Attestation", "SRC"],
    cta:          "Current Plan",
    tier_key:     "free",
    package_id:   null,
    color:        "#94A3B8",
  },
  {
    key:          "professional",
    name:         "Professional",
    price:        "$299",
    interval:     "/month",
    badge:        "Core Product",
    highlight:    true,
    description:  "External reconciliation, portfolio reporting, and submission packages",
    tagline:      "Reconcile and report",
    features: [
      "External Registry Reconciliation (ERR) — field-level",
      "Portfolio Reconciliation Report (live + export)",
      "Frozen portfolio recon snapshots (PRS)",
      "CA Status Workflow + submission tracking",
      "BTR-ready JSON/CSV exports",
      "Bilateral agreement registry",
      "Unlimited projects",
    ],
    not_included: ["Dual attestation", "Audit snapshots", "Submission Readiness Certificate"],
    cta:          "Upgrade to Professional",
    tier_key:     "professional",
    package_id:   "professional_monthly",
    color:        "#3B82F6",
  },
  {
    key:          "institutional",
    name:         "Institutional",
    price:        "$999",
    interval:     "/month",
    badge:        "Flagship",
    highlight:    false,
    description:  "Full governance infrastructure for sovereign, DFI, and regulatory workflows",
    tagline:      "Prove submission readiness",
    features: [
      "Submission Readiness Certificate (SRC)",
      "Multi-signer dual attestation (org-level authority)",
      "CPIE audit snapshots — versioned, immutable",
      "Exception Management System (EMS) full access",
      "Governance scheduler + risk-based watch layer",
      "Cross-project integrity engine (5 rules)",
      "Organization-level signing authority context",
      "Regulator-ready export packages",
      "All Professional tier features",
    ],
    not_included: [],
    cta:          "Upgrade to Institutional",
    tier_key:     "institutional",
    package_id:   "institutional_monthly",
    color:        "#059669",
  },
];

const ORG_TYPES = [
  { value: "corporate",             label: "Corporate (Industrial / Energy / Construction)" },
  { value: "financial_institution", label: "Financial Institution (Bank / DFI / Investor)" },
  { value: "government",            label: "Government / Public Agency" },
  { value: "project_developer",     label: "Project Developer" },
  { value: "other",                 label: "Other" },
];
const USE_CASES = [
  { value: "portfolio_submission",      label: "Portfolio submission readiness (Article 6 / BTR)" },
  { value: "carbon_asset_dev",          label: "Carbon asset development & certification" },
  { value: "investment_due_diligence",  label: "Investment due diligence" },
  { value: "esg_compliance",            label: "Climate reporting / ESG compliance" },
  { value: "other",                     label: "Other" },
];
const PROJECT_RANGES = ["1–5", "5–20", "20+"];
const VOLUME_RANGES  = ["<100t", "100–1,000t", ">1,000t"];
const URGENCY_OPTIONS = [
  { value: "immediately", label: "Immediately (0–3 months)" },
  { value: "near_term",   label: "Near term (3–6 months)" },
  { value: "exploring",   label: "Exploring / no fixed timeline" },
];

function EnterpriseInquiryForm({ onClose }) {
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", role_title: "", organization: "",
    org_type: "", use_cases: [], project_count_range: "", geography: "",
    volume_range: "", urgency: "", message: "",
  });
  const [submitting,   setSubmitting]   = useState(false);
  const [result,       setResult]       = useState(null);
  const [emailWarning, setEmailWarning] = useState(false);

  const PERSONAL = ["gmail","yahoo","hotmail","outlook","icloud","live","protonmail","aol","gmx","mail.com"];
  const checkEmail = (email) => {
    const domain = email.split("@").pop() || "";
    setEmailWarning(PERSONAL.some(d => domain.includes(d)));
  };

  const toggleUseCase = (val) => {
    setForm(f => ({
      ...f,
      use_cases: f.use_cases.includes(val)
        ? f.use_cases.filter(v => v !== val)
        : [...f.use_cases, val],
    }));
  };

  const handleSubmit = async () => {
    if (!form.contact_name.trim() || !form.contact_email.trim() || !form.organization.trim()) {
      toast.warning("Name, email, and organization are required.");
      return;
    }
    if (emailWarning) {
      toast.warning("Please use your work or institutional email address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        project_count_range: form.project_count_range || undefined,
        volume_range:        form.volume_range        || undefined,
        urgency:             form.urgency             || undefined,
        org_type:            form.org_type            || undefined,
      };
      const res = await client.post("/billing/enterprise-inquiry", payload);
      setResult(res.data);
    } catch (err) {
      const d = err.response?.data;
      if (typeof d === "object" && d.detail?.message) {
        toast.error(d.detail.message);
      } else {
        toast.error(err.response?.data?.detail || "Submission failed");
      }
    }
    setSubmitting(false);
  };

  // Confirmation state
  if (result) {
    const isHighValue = ["enterprise","institutional"].includes(result.qualification_tier);
    return (
      <div className="space-y-4 text-center py-4" data-testid="inquiry-confirmation">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto ${isHighValue ? "bg-emerald-100" : "bg-blue-100"}`}>
          <CheckCircle2 size={24} className={isHighValue ? "text-emerald-600" : "text-blue-500"} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">{result.inquiry_id} — Inquiry Received</p>
          <p className="text-xs text-slate-500 mt-1">{result.response_timeline}</p>
        </div>
        {result.fit_note && (
          <div className={`rounded-xl px-4 py-3 border text-xs ${isHighValue ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-blue-50 border-blue-200 text-blue-700"}`}
            data-testid="fit-recommendation">
            <p className="font-semibold mb-0.5">Fit Assessment</p>
            <p>{result.fit_note}</p>
          </div>
        )}
        <div className="text-left space-y-1.5">
          {(result.next_steps || []).map((step, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-500">
              <span className="text-xs font-bold text-slate-300 flex-shrink-0 w-4">{i + 1}.</span>
              {step}
            </div>
          ))}
        </div>
        <button onClick={onClose} className="w-full h-9 rounded-xl bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="enterprise-inquiry-form">
      {/* Trust signal */}
      <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
        <Shield size={10} className="text-teal-500 flex-shrink-0" />
        Used for institutional-grade climate asset governance by corporates, project developers, and government agencies.
      </div>

      {/* Section 1: Identity */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Contact</p>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Full Name *</label>
            <input value={form.contact_name} onChange={e => setForm(f=>({...f,contact_name:e.target.value}))}
              placeholder="Dr. Amara Osei"
              className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
              data-testid="enq-name" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Work Email *</label>
            <input value={form.contact_email}
              onChange={e => { setForm(f=>({...f,contact_email:e.target.value})); checkEmail(e.target.value); }}
              placeholder="name@organization.org"
              className={`w-full h-9 px-3 text-xs border rounded-lg focus:outline-none ${emailWarning ? "border-red-300 focus:border-red-400 bg-red-50" : "border-slate-200 focus:border-teal-400"}`}
              data-testid="enq-email" />
            {emailWarning && <p className="text-xs text-red-500 mt-0.5">Please use your work email</p>}
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Role / Title</label>
            <input value={form.role_title} onChange={e => setForm(f=>({...f,role_title:e.target.value}))}
              placeholder="Head of Sustainability"
              className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
              data-testid="enq-role" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Organization *</label>
            <input value={form.organization} onChange={e => setForm(f=>({...f,organization:e.target.value}))}
              placeholder="Your organization name"
              className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
              data-testid="enq-org" />
          </div>
        </div>
        {/* Org type */}
        <div className="mt-2.5">
          <label className="text-xs text-slate-500 mb-0.5 block">Organization Type *</label>
          <select value={form.org_type} onChange={e => setForm(f=>({...f,org_type:e.target.value}))}
            className="w-full h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-teal-400"
            data-testid="enq-org-type">
            <option value="">Select type...</option>
            {ORG_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Section 2: Use case */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Use Case (select all that apply)</p>
        <div className="space-y-1.5">
          {USE_CASES.map(uc => (
            <label key={uc.value} className="flex items-center gap-2.5 cursor-pointer group"
              data-testid={`enq-usecase-${uc.value}`}>
              <div className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                form.use_cases.includes(uc.value) ? "bg-teal-500 border-teal-500" : "bg-white border-slate-300 group-hover:border-teal-400"
              }`}
                onClick={() => toggleUseCase(uc.value)}>
                {form.use_cases.includes(uc.value) && <Check size={10} className="text-white" strokeWidth={3} />}
              </div>
              <span className="text-xs text-slate-600" onClick={() => toggleUseCase(uc.value)}>{uc.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Section 3: Scope */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Portfolio / Project Scope</p>
        <div className="grid grid-cols-3 gap-2.5">
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Number of Projects</label>
            <select value={form.project_count_range} onChange={e => setForm(f=>({...f,project_count_range:e.target.value}))}
              className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
              data-testid="enq-project-range">
              <option value="">Select...</option>
              {PROJECT_RANGES.map(r => <option key={r} value={r}>{r} projects</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Annual Volume</label>
            <select value={form.volume_range} onChange={e => setForm(f=>({...f,volume_range:e.target.value}))}
              className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none"
              data-testid="enq-volume-range">
              <option value="">Select...</option>
              {VOLUME_RANGES.map(r => <option key={r} value={r}>{r} tCO₂e</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Geography</label>
            <input value={form.geography} onChange={e => setForm(f=>({...f,geography:e.target.value}))}
              placeholder="e.g. Nigeria, Kenya, SA"
              className="w-full h-9 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none"
              data-testid="enq-geography" />
          </div>
        </div>
      </div>

      {/* Section 4: Urgency */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Timeline</p>
        <div className="flex gap-2 flex-wrap">
          {URGENCY_OPTIONS.map(u => (
            <button key={u.value}
              onClick={() => setForm(f=>({...f,urgency:u.value}))}
              data-testid={`enq-urgency-${u.value}`}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                form.urgency === u.value
                  ? "bg-teal-500 border-teal-500 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-teal-300"
              }`}>
              {u.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="text-xs text-slate-500 mb-0.5 block">Additional Context (optional)</label>
        <textarea value={form.message} onChange={e => setForm(f=>({...f,message:e.target.value}))}
          placeholder="Any specific governance requirements, existing registries, or constraints we should know about..."
          rows={2}
          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-teal-400"
          data-testid="enq-message" />
      </div>

      <button onClick={handleSubmit} disabled={submitting || emailWarning}
        className="w-full h-10 rounded-xl bg-[#0F172A] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-700 disabled:opacity-50"
        data-testid="enq-submit-btn">
        {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Building2 size={14} />}
        {submitting ? "Submitting..." : "Submit Enterprise Inquiry"}
      </button>

      <p className="text-xs text-slate-400 text-center">
        We review each inquiry individually. Not every submission will qualify for the Enterprise program.
        Personal email addresses are not accepted.
      </p>
    </div>
  );
}

function TierCard({ tier, currentTier, onUpgrade, upgrading }) {
  const isCurrent = currentTier === tier.tier_key ||
    (currentTier === "pro" && tier.tier_key === "professional");
  const isDowngrade = (
    (currentTier === "institutional" && tier.tier_key !== "institutional") ||
    (currentTier === "professional" && tier.tier_key === "free")
  );

  return (
    <div className={`relative bg-white rounded-xl border-2 overflow-hidden flex flex-col ${
      tier.highlight ? "border-blue-400 shadow-lg shadow-blue-100" : "border-slate-200"
    }`} data-testid={`tier-card-${tier.key}`}>
      {tier.badge && (
        <div className="absolute top-0 right-0 text-xs font-bold px-3 py-1 text-white"
          style={{ backgroundColor: tier.color }}>
          {tier.badge}
        </div>
      )}

      <div className="p-6 flex-1">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: tier.color }}>{tier.name}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">
            {tier.price}
            {tier.interval && <span className="text-base font-normal text-slate-400">{tier.interval}</span>}
          </p>
          <p className="text-xs text-slate-400 mt-1">{tier.description}</p>
          <p className="text-sm font-semibold text-slate-900 mt-2 italic">"{tier.tagline}"</p>
        </div>

        <div className="space-y-2 mb-5">
          {tier.features.map(f => (
            <div key={f} className="flex items-start gap-2 text-xs">
              <CheckCircle2 size={11} className="flex-shrink-0 mt-0.5" style={{ color: tier.color }} strokeWidth={2.5} />
              <span className="text-slate-600">{f}</span>
            </div>
          ))}
          {tier.not_included.map(f => (
            <div key={f} className="flex items-start gap-2 text-xs opacity-40">
              <X size={11} className="text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <span className="text-slate-400">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 pt-0">
        {isCurrent ? (
          <div className="h-10 rounded-xl bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center">
            Current Plan
          </div>
        ) : isDowngrade ? (
          <div className="h-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-400 text-xs flex items-center justify-center">
            Included in current plan
          </div>
        ) : tier.package_id ? (
          <button
            onClick={() => onUpgrade(tier.package_id)}
            disabled={upgrading}
            data-testid={`upgrade-btn-${tier.key}`}
            className="w-full h-10 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all"
            style={{ backgroundColor: tier.color }}>
            {upgrading ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
            {upgrading ? "Redirecting to Stripe..." : tier.cta}
          </button>
        ) : (
          <button disabled className="w-full h-10 rounded-xl bg-slate-100 text-slate-400 text-xs font-medium">
            {tier.cta}
          </button>
        )}
      </div>
    </div>
  );
}

export default function UpgradePage() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const [params]   = useSearchParams();
  const [upgrading, setUpgrading] = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [showEnterprise, setShowEnterprise] = useState(false);

  const currentTier = user?.subscription_tier || "free";

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = params.get("session_id");
    const payment   = params.get("payment");
    if (payment === "cancelled") {
      toast.warning("Payment cancelled. You were not charged.");
      return;
    }
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [params]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 8;
    if (attempt >= maxAttempts) {
      setStatusMsg({ type: "warning", text: "Payment status check timed out. Check your email for confirmation." });
      return;
    }
    setChecking(true);
    try {
      const res = await client.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatusMsg({ type: "success", text: `Payment successful! Your plan has been upgraded. Welcome to ${res.data.subscription_tier}.` });
        toast.success("Subscription activated!");
        setChecking(false);
        // Refresh page to update user tier
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch {
      setChecking(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    setUpgrading(true);
    try {
      const originUrl = window.location.origin;
      const res = await client.post("/payments/checkout", {
        origin_url: originUrl,
        package_id: packageId,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("No checkout URL returned");
        setUpgrading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setUpgrading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 min-h-screen" style={{ backgroundColor: "#F8FAFC" }} data-testid="upgrade-page">
      {/* Header */}
      <div className="text-centermb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-200 mb-4">
          <Shield size={12} className="text-teal-600" />
          <span className="text-xs font-semibold text-teal-700">Submission-Readiness Infrastructure for Climate Assets</span>
        </div>
        <PageHeader title="From measurement to submission-ready" />

      <MetricCard label="Available Plans" value={plans.length} color="text-slate-900" />
        <p className="text-slate-500 text-sm leading-relaxed">
          DeCarbonIQ is governance infrastructure — not a marketplace, registry, or MRV tool.
          <br />
          It makes climate assets <strong>internally defensible</strong> and <strong>externally reconcilable</strong>.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Current plan: <strong className="text-slate-900 capitalize">{currentTier}</strong>
        </p>
      </div>

      {/* Status message */}
      {statusMsg && (
        <div className={`max-w-lg mx-auto mb-6 rounded-xl p-4 flex items-start gap-2 ${
          statusMsg.type === "success" ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
        }`}>
          <CheckCircle2 size={15} className={statusMsg.type === "success" ? "text-emerald-600" : "text-amber-500"} />
          <p className={`text-xs font-medium ${statusMsg.type === "success" ? "text-emerald-700" : "text-amber-700"}`}>
            {statusMsg.text}
          </p>
        </div>
      )}
      {checking && (
        <div className="max-w-lg mx-auto mb-6 text-center text-xs text-slate-500">
          <RefreshCw size={12} className="animate-spin inline mr-1" /> Verifying payment status...
        </div>
      )}

      {/* Tier cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TIERS.map(tier => (
          <TierCard key={tier.key} tier={tier} currentTier={currentTier}
            onUpgrade={handleUpgrade} upgrading={upgrading} />
        ))}
      </div>

      {/* Enterprise CTA with inline qualified form */}
      <div className="mt-6">
        {!showEnterprise ? (
          <div className="bg-[#0F172A] rounded-xl p-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-white font-bold">Enterprise / Government / DFI</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Custom pricing for host country ministries, development banks, and large corporates.
                Includes custom governance architecture, dedicated onboarding, and future UNFCCC API integration.
              </p>
              <p className="text-slate-500 text-xs mt-1 italic">
                Not everyone qualifies. We review each submission individually.
              </p>
            </div>
            <button onClick={() => setShowEnterprise(true)} data-testid="open-enterprise-form-btn"
              className="flex-shrink-0 flex items-center gap-2 px-5 h-10 bg-white text-slate-900 text-sm font-semibold rounded-xl hover:bg-slate-100 whitespace-nowrap">
              <Building2 size={14} /> Apply for Enterprise
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-[#0F172A] rounded-xl overflow-hidden">
            <div className="bg-[#0F172A] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-white font-bold">Enterprise / DFI / Government Application</p>
                <p className="text-slate-400 text-xs mt-0.5">Qualified intake — we review each submission individually</p>
              </div>
              <button onClick={() => setShowEnterprise(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <EnterpriseInquiryForm onClose={() => setShowEnterprise(false)} />
            </div>
          </div>
        )}
      </div>

      {/* Positioning note */}
      <div className="mt-6 text-center">
        <p className="text-xs text-slate-400">
          DeCarbonIQ is infrastructure for making climate assets submission-ready and audit-defensible.
          It is not a carbon marketplace, MRV tool, or registry.
          Stripe payments are processed securely via Stripe. Subscriptions renew monthly.
        </p>
      </div>
    </div>
  );
}
